import React, { useState, useEffect } from "react";
import { Card, Button, Spinner, Form, Modal } from "react-bootstrap";
import QuickQuizService, { MonitoringData, MonitoringStudent } from "../../_services/QuickQuizService";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const mon = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${day}-${mon}-${year} ${hours}:${mins}`;
};

interface TestMonitoringPanelProps {
  testId: number;
  onBack: () => void;
  onViewTest?: (testId: number) => void;
}

// Plain <span> pills (Bootstrap Badge is banned in this codebase — global .badge
// overrides render it oversized). Soft-tint background + status dot.
const STATUS_STYLES: Record<string, { bg: string; color: string; dot: string }> = {
  "Submitted": { bg: "#dcfce7", color: "var(--status-success-text)", dot: "#22c55e" },
  "In Progress": { bg: "#fef3c7", color: "var(--status-warning-text)", dot: "#f59e0b" },
  "Not Started": { bg: "#fee2e2", color: "var(--status-danger)", dot: "#ef4444" },
  "Assigned": { bg: "#e0f2fe", color: "#0369a1", dot: "#0ea5e9" },
};

const StatusBadge: React.FC<{ label: string }> = ({ label }) => {
  const s = STATUS_STYLES[label] || { bg: "#f1f5f9", color: "var(--text-body)", dot: "#94a3b8" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 11px",
        borderRadius: "999px",
        fontSize: "0.72rem",
        fontWeight: 600,
        background: s.bg,
        color: s.color,
        whiteSpace: "nowrap",
        lineHeight: 1.4,
      }}
    >
      <span
        className={label === "In Progress" ? "qqm-dot qqm-pulse" : "qqm-dot"}
        style={{ background: s.dot }}
      />
      {label}
    </span>
  );
};

const AVATAR_COLORS = [
  "var(--gradient-brand)",
  "linear-gradient(135deg,#0ea5e9,#06b6d4)",
  "linear-gradient(135deg,#f59e0b,#f97316)",
  "linear-gradient(135deg,#10b981,#059669)",
  "linear-gradient(135deg,#ec4899,#db2777)",
  "linear-gradient(135deg,#14b8a6,#0d9488)",
];
const avatarColor = (name: string): string =>
  AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const StatCard: React.FC<{ label: string; value: number | string; color: string }> = ({ label, value, color }) => (
  <div
    style={{
      flex: "1 1 120px",
      background: color,
      borderRadius: "8px",
      padding: "12px 16px",
      color: "var(--text-on-brand)",
      textAlign: "center",
      minWidth: "120px",
      margin: "2px",
    }}
  >
    <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{value}</div>
    <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>{label}</div>
  </div>
);

const TestMonitoringPanel: React.FC<TestMonitoringPanelProps> = ({ testId, onBack, onViewTest }) => {
  const [data, setData] = useState<MonitoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // Audit modal states
  const [auditModal, setAuditModal] = useState<{ show: boolean; student: MonitoringStudent | null; data: any }>({ show: false, student: null, data: null });
  const [dynamoModal, setDynamoModal] = useState<{ show: boolean; student: MonitoringStudent | null; data: any }>({ show: false, student: null, data: null });
  const [auditLoading, setAuditLoading] = useState(false);

  const handleShowAudit = async (student: MonitoringStudent) => {
    setAuditLoading(true);
    setAuditModal({ show: true, student, data: null });
    try {
      const result = await QuickQuizService.getStudentAuditTrail(testId, student.enquiryId);
      setAuditModal({ show: true, student, data: result });
    } catch {
      setAuditModal({ show: true, student, data: { error: "Failed to load audit data" } });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleShowDynamo = async (student: MonitoringStudent) => {
    setAuditLoading(true);
    setDynamoModal({ show: true, student, data: null });
    try {
      const result = await QuickQuizService.getStudentDynamoResponses(testId, student.enquiryId);
      setDynamoModal({ show: true, student, data: result });
    } catch {
      setDynamoModal({ show: true, student, data: { error: "Failed to load data" } });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleDyna2Rds = async (student: MonitoringStudent) => {
    try {
      const result = await QuickQuizService.dyna2Rds(testId, student.enquiryId);
      // eslint-disable-next-line no-restricted-globals
      window.alert(result.message || "Data copied successfully");
      // Success: close the responses modal and refresh so the student's status
      // reflects the newly finalized submission.
      setDynamoModal({ show: false, student: null, data: null });
      await loadData();
    } catch (err: any) {
      // sendError returns { error: { message } }; keep the flat .message fallback
      // for older-shaped responses.
      const msg = err?.response?.data?.error?.message
        || err?.response?.data?.message
        || "Failed to copy data";
      // eslint-disable-next-line no-restricted-globals
      window.alert(msg);
    }
  };

  // Opens the (Node) web-cam proctoring screen for this student in a new tab —
  // the React port of the Java WebCamProctoring screen (getWebProctoring).
  const handleWebProctoring = (student: MonitoringStudent) => {
    const url = `${window.location.origin}/quick-quiz-maker?mode=web-proctoring&testId=${testId}&enquiryId=${student.enquiryId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handlePopulate = async (student: MonitoringStudent) => {
    try {
      const result = await QuickQuizService.populateStudentData(testId, student.enquiryId);
      // eslint-disable-next-line no-restricted-globals
      window.alert(result.message || "Data populated successfully");
      loadData();
    } catch (err: any) {
      // eslint-disable-next-line no-restricted-globals
      window.alert(err?.response?.data?.message || "Failed to populate data");
    }
  };

  const handleDeleteResponse = async (student: MonitoringStudent) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Are you sure you want to delete test responses for ${student.studentName}?`)) return;
    try {
      await QuickQuizService.deleteStudentResponses(testId, student.enquiryId);
      // eslint-disable-next-line no-restricted-globals
      window.alert("Responses deleted successfully");
      loadData();
    } catch (err: any) {
      // eslint-disable-next-line no-restricted-globals
      window.alert(err?.response?.data?.message || "Failed to delete responses");
    }
  };

  const loadData = async () => {
    try {
      const result = await QuickQuizService.getTestMonitoring(testId);
      setData(result);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to load monitoring data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (autoRefresh) {
      refreshRef.current = setInterval(loadData, 10000);
    } else {
      if (refreshRef.current) clearInterval(refreshRef.current);
      refreshRef.current = null;
    }
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [autoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner role="status" animation="border" />
        <p className="text-muted mt-2">Loading monitoring data...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <Card.Body className="text-center py-4">
          <p className="text-danger">{error || "Failed to load monitoring data"}</p>
          <Button variant="secondary" size="sm" onClick={onBack} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>
            Back
          </Button>
        </Card.Body>
      </Card>
    );
  }

  const { test, stats, students } = data;

  // Filter students
  let filtered: MonitoringStudent[] = students;
  if (statusFilter !== "all") {
    filtered = filtered.filter((s) => s.statusLabel === statusFilter);
  }
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (s) =>
        s.studentName.toLowerCase().includes(q) ||
        s.studentCode.toLowerCase().includes(q) ||
        String(s.enquiryId).includes(q)
    );
  }

  return (
    <>
      {/* Header */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            <strong>Test Monitoring</strong>
            <span style={{ margin: "0 8px", color: "var(--text-muted)" }}>
              {test.name} (ID: {test.testId})
            </span>
          </span>
          <div className="d-flex gap-2 align-items-center">
            <Form.Check
              type="switch"
              label="Auto-refresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ fontSize: "0.8rem", marginRight: "8px" }}
            />
            {onViewTest && (
              <Button variant="outline-primary" size="sm" onClick={() => onViewTest(testId)} style={{ backgroundColor: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}>
                View Test
              </Button>
            )}
            <Button
              variant="outline-primary"
              size="sm"
              style={{ backgroundColor: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}
              onClick={() => { setLoading(true); loadData(); }}
            >
              Refresh
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={onBack} style={{ backgroundColor: "var(--surface-card)", color: "var(--text-muted)", border: "1px solid #6c757d", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}>
              Back
            </Button>
          </div>
        </Card.Header>
      </Card>

      {/* Stats Cards */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <StatCard label="Total Students" value={stats.totalStudents} color="var(--text-muted)" />
        <StatCard label="Submitted" value={stats.submitted} color="var(--status-success)" />
        <StatCard label="In Progress" value={stats.inProgress} color="var(--status-warning)" />
        <StatCard label="Not Started" value={stats.notStarted} color="var(--status-danger)" />
        <StatCard label="Average Marks" value={`${stats.averageMarks} / ${test.totalMarks}`} color="var(--status-info)" />
        <StatCard label="Highest" value={stats.highestMarks} color="var(--brand-primary)" />
        <StatCard label="Lowest" value={stats.lowestMarks} color="#6610f2" />
      </div>

      {/* Filters */}
      <Card className="qqm-filter-card mb-3">
        <Card.Body style={{ padding: "12px 16px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
            <div className="qqm-search">
              <span className="qqm-search-icon">&#128269;</span>
              <input
                className="qqm-search-input"
                placeholder="Search by name, code, or ID…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="qqm-select-wrap">
              <select
                className="qqm-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="Submitted">Submitted</option>
                <option value="In Progress">In Progress</option>
                <option value="Not Started">Not Started</option>
                <option value="Assigned">Assigned</option>
              </select>
              <span className="qqm-select-chevron">&#9662;</span>
            </div>
            <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginLeft: "auto" }}>
              Showing <strong style={{ color: "var(--text-strong)" }}>{filtered.length}</strong> of {students.length} students
            </span>
          </div>
        </Card.Body>
      </Card>

      {/* Audit Trail Modal (redesigned to match Results page) */}
      <Modal
        show={auditModal.show}
        onHide={() => setAuditModal({ show: false, student: null, data: null })}
        size="lg"
        centered
        dialogClassName="audit-modal-dialog"
      >
        <style>{`
          .audit-modal-dialog .modal-content { border: none; border-radius: 14px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
          .audit-modal-dialog .modal-header { background: linear-gradient(135deg, #1e293b 0%, #334155 100%); border-bottom: none; padding: 16px 22px; }
          .audit-modal-dialog .modal-header .btn-close { filter: invert(1) brightness(2); opacity: 0.85; }
          .audit-modal-dialog .modal-body { background: #f8fafc; padding: 20px 22px; max-height: 75vh; overflow-y: auto; }
          .audit-modal-dialog .modal-footer { background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 12px 22px; }
          .audit-chip { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:999px; font-size:0.72rem; font-weight:600; }
        `}</style>
        <Modal.Header closeButton style={{ color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "0.98rem", fontWeight: 600, color: "var(--text-on-brand)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background: "var(--gradient-brand)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {auditModal.student?.studentName?.[0]?.toUpperCase() || "A"}
              </span>
              <div style={{ lineHeight: 1.2 }}>
                <div>{auditModal.student?.studentName || ""}</div>
                <div style={{ fontSize: "0.72rem", opacity: 0.75, fontWeight: 400 }}>
                  {test?.name || ""} · Test #{testId} · Audit Trail
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {auditLoading ? (
            <div className="text-center py-4"><Spinner role="status" animation="border" size="sm" /> <span style={{ marginLeft: "8px", color: "var(--text-muted)" }}>Loading audit trail…</span></div>
          ) : auditModal.data?.error ? (
            <div style={{ padding: "12px 16px", background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", borderRadius: "10px", color: "var(--status-danger)" }}>
              {auditModal.data.error}
            </div>
          ) : auditModal.data ? (
            <>
              {/* Device Info Card */}
              <div
                style={{
                  background: "var(--surface-card)",
                  border: "1px solid var(--border-default)",
                  borderRadius: "12px",
                  padding: "14px 18px",
                  marginBottom: "14px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                  Device & Connection
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  <span className="audit-chip" style={{ background: "var(--brand-primary-subtle)", color: "var(--brand-primary-hover)", border: "1px solid var(--brand-primary-subtle)" }}>
                    <span style={{ fontSize: "0.85rem" }}>&#128187;</span> {auditModal.data.deviceInfo?.os || "Unknown OS"}
                  </span>
                  <span className="audit-chip" style={{ background: "#ecfeff", color: "#0e7490", border: "1px solid #a5f3fc" }}>
                    <span style={{ fontSize: "0.85rem" }}>&#127760;</span> {auditModal.data.deviceInfo?.browser || "Unknown browser"}
                  </span>
                  <span className="audit-chip" style={{ background: "var(--status-warning-bg)", color: "var(--status-warning-text)", border: "1px solid var(--status-warning-border)" }}>
                    <span style={{ fontSize: "0.85rem" }}>&#128225;</span> {auditModal.data.deviceInfo?.ip || "No IP"}
                  </span>
                </div>
              </div>

              {/* Verdict banner — one clear answer: student submitted vs staff populated */}
              {auditModal.data.submitVerdict && (() => {
                const v = auditModal.data.submitVerdict;
                const palette = v.type === "student"
                  ? { bg: "var(--status-success-bg)", text: "var(--status-success-text)", border: "var(--status-success-border)", accent: "var(--status-success)", icon: "✔", tag: "STUDENT SUBMITTED" }
                  : v.type === "staff"
                    ? { bg: "var(--status-warning-bg)", text: "var(--status-warning-text)", border: "var(--status-warning-border)", accent: "var(--status-warning)", icon: "⚠", tag: "STAFF POPULATED" }
                    : { bg: "var(--surface-muted)", text: "var(--text-muted)", border: "var(--border-default)", accent: "var(--text-faint)", icon: "—", tag: "NO SUBMISSION" };
                return (
                  <div style={{ background: palette.bg, border: `1px solid ${palette.border}`, borderLeft: `4px solid ${palette.accent}`, borderRadius: "10px", padding: "12px 16px", marginBottom: "14px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <span style={{ fontSize: "1.15rem", lineHeight: 1.2, color: palette.accent, flexShrink: 0 }}>{palette.icon}</span>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                        <span style={{ padding: "2px 8px", borderRadius: "4px", background: palette.accent, color: "var(--text-on-brand, #fff)", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.4px" }}>{palette.tag}</span>
                        <span style={{ fontWeight: 700, fontSize: "0.9rem", color: palette.text }}>{v.title}</span>
                      </div>
                      <div style={{ fontSize: "0.82rem", color: palette.text, lineHeight: 1.5 }}>{v.detail}</div>
                    </div>
                  </div>
                );
              })()}

              {/* Submit Status Cards — supporting technical detail behind the verdict */}
              {(auditModal.data.dynamoSubmitStatus || auditModal.data.rdsSubmitStatus) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px", marginBottom: "14px" }}>
                  {auditModal.data.dynamoSubmitStatus && (
                    <div
                      style={{
                        background: "var(--surface-card)",
                        border: "1px solid var(--border-default)",
                        borderLeft: "4px solid var(--status-warning)",
                        borderRadius: "10px",
                        padding: "12px 16px",
                        fontSize: "0.82rem",
                        color: "var(--text-strong)",
                        display: "flex",
                        gap: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          background: "var(--status-warning-bg)",
                          color: "var(--status-warning-text)",
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                          flexShrink: 0,
                        }}
                      >
                        DYNAMO DB
                      </span>
                      <span style={{ lineHeight: 1.5 }}>{auditModal.data.dynamoSubmitStatus}</span>
                    </div>
                  )}
                  {auditModal.data.rdsSubmitStatus && (
                    <div
                      style={{
                        background: "var(--surface-card)",
                        border: "1px solid var(--border-default)",
                        borderLeft: "4px solid #0891b2",
                        borderRadius: "10px",
                        padding: "12px 16px",
                        fontSize: "0.82rem",
                        color: "var(--text-strong)",
                        display: "flex",
                        gap: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          background: "#cffafe",
                          color: "#0e7490",
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                          flexShrink: 0,
                        }}
                      >
                        RDS
                      </span>
                      <span style={{ lineHeight: 1.5 }}>{auditModal.data.rdsSubmitStatus}</span>
                    </div>
                  )}
                </div>
              )}

              {/* User Actions Timeline */}
              <div
                style={{
                  background: "var(--surface-card)",
                  border: "1px solid var(--border-default)",
                  borderRadius: "12px",
                  padding: "14px 18px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>User Action Timeline</span>
                  {auditModal.data.userActions?.length > 0 && (
                    <span style={{ fontSize: "0.7rem", color: "var(--text-faint)", fontWeight: 600 }}>
                      {auditModal.data.userActions.length} event{auditModal.data.userActions.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                {auditModal.data.userActions?.length > 0 ? (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {auditModal.data.userActions.map((a: any, i: number) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          padding: "8px 0",
                          borderBottom: i < auditModal.data.userActions.length - 1 ? "1px solid #f1f5f9" : "none",
                          fontSize: "0.82rem",
                        }}
                      >
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: "var(--brand-primary)",
                            marginTop: "6px",
                            flexShrink: 0,
                            boxShadow: "0 0 0 3px rgba(99,102,241,0.15)",
                          }}
                        />
                        <div style={{ flex: 1, display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
                          <span style={{ color: "var(--text-strong)", fontWeight: 500 }}>{a.action}</span>
                          <span style={{ color: "var(--text-muted)", fontSize: "0.75rem", fontFamily: "monospace", whiteSpace: "nowrap" }}>{a.timestamp}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "20px 0", color: "var(--text-faint)", fontSize: "0.85rem" }}>
                    No user actions recorded.
                  </div>
                )}
              </div>

              {/* History (RDS) */}
              {auditModal.data.historyEntries?.length > 0 && (
                <div
                  style={{
                    background: "var(--surface-card)",
                    border: "1px solid var(--border-default)",
                    borderRadius: "12px",
                    padding: "14px 18px",
                    marginTop: "14px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                    RDS History
                  </div>
                  <pre style={{ fontSize: "0.75rem", background: "var(--surface-page)", padding: "10px 12px", borderRadius: "8px", border: "1px solid var(--border-default)", maxHeight: "200px", overflow: "auto", margin: 0, color: "var(--text-strong)" }}>
                    {JSON.stringify(auditModal.data.historyEntries, null, 2)}
                  </pre>
                </div>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            onClick={() => setAuditModal({ show: false, student: null, data: null })}
            style={{ backgroundColor: "#475569", color: "var(--text-on-brand)", border: "none", borderRadius: "8px", padding: "7px 18px", fontWeight: 600 }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DynamoDB Responses Modal */}
      <Modal show={dynamoModal.show} onHide={() => setDynamoModal({ show: false, student: null, data: null })} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: "#343a40", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "0.95rem" }}>
            {dynamoModal.student?.studentName} || {test.name}[{testId}] Audit
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {auditLoading ? (
            <div className="text-center py-3"><Spinner role="status" animation="border" size="sm" /> Loading...</div>
          ) : dynamoModal.data?.error ? (
            <p className="text-danger">{dynamoModal.data.error}</p>
          ) : dynamoModal.data ? (
            <>
              <h6 style={{ fontSize: "0.85rem", fontWeight: 700, textDecoration: "underline" }}>Test Responses</h6>
              {dynamoModal.data.responses?.length > 0 ? (
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <table className="table table-sm table-bordered" style={{ fontSize: "0.75rem" }}>
                    <thead className="table-light"><tr><th>Section</th><th>Question</th><th>Attempted Choice</th><th>Attempt Choice String</th><th>Timestamp</th><th>Time Spent</th><th>Status</th></tr></thead>
                    <tbody>
                      {dynamoModal.data.responses.map((r: any, i: number) => (
                        <tr key={i}><td>{r.sectionId}</td><td>{r.questionId}</td><td>{r.attemptedChoiceId || "-"}</td><td>{r.attemptedChoiceString || "-"}</td><td>{r.timestamp}</td><td>{r.timeSpent}</td><td>{r.status}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-muted" style={{ fontSize: "0.8rem" }}>No responses found</p>}

              <h6 style={{ fontSize: "0.85rem", fontWeight: 700, textDecoration: "underline", marginTop: "12px" }}>User Details</h6>
              <p style={{ fontSize: "0.8rem", margin: "4px 0" }}><strong>Timer:</strong> {dynamoModal.data.timer || "-"}</p>
              <p style={{ fontSize: "0.8rem", margin: "4px 0" }}><strong>Uploads:</strong> {dynamoModal.data.uploads || "-"}</p>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => { if (dynamoModal.student) handleDyna2Rds(dynamoModal.student); }} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>Dyna {'>>>'} RDS</Button>
          <Button size="sm" onClick={() => { if (dynamoModal.student) handleDeleteResponse(dynamoModal.student); }} style={{ backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "1px solid var(--status-danger)", margin: "2px" }}>Delete Responses</Button>
          <Button size="sm" onClick={() => setDynamoModal({ show: false, student: null, data: null })} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Student Table */}
      <style>{`
        .qqm-filter-card { border: none; border-radius: 14px;
          box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 12px 28px -16px rgba(15,23,42,0.2); }
        .qqm-search { position: relative; flex: 1 1 280px; max-width: 340px; min-width: 200px; }
        .qqm-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          font-size: 0.82rem; opacity: 0.5; pointer-events: none; }
        .qqm-search-input { width: 100%; height: 38px; padding: 0 12px 0 34px; font-size: 0.85rem;
          color: #0f172a; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
          outline: none; transition: all 0.15s ease; }
        .qqm-search-input::placeholder { color: #94a3b8; }
        .qqm-search-input:focus { background: #fff; border-color: #818cf8;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.15); }
        .qqm-select-wrap { position: relative; }
        .qqm-select { height: 38px; padding: 0 34px 0 14px; font-size: 0.85rem; font-weight: 500;
          color: #0f172a; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
          outline: none; cursor: pointer; appearance: none; -webkit-appearance: none; -moz-appearance: none;
          transition: all 0.15s ease; }
        .qqm-select:hover { border-color: #cbd5e1; }
        .qqm-select:focus { background: #fff; border-color: #818cf8;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.15); }
        .qqm-select-chevron { position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
          font-size: 0.7rem; color: #64748b; pointer-events: none; }
        .qqm-monitor-card { border: none; border-radius: 14px; overflow: hidden;
          box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 12px 28px -16px rgba(15,23,42,0.25); }
        .qqm-table { width: 100%; border-collapse: separate; border-spacing: 0; margin: 0; }
        .qqm-table thead th { background: #f8fafc; color: #64748b; font-size: 0.68rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.6px; padding: 13px 16px;
          border-bottom: 2px solid #e9eef5; white-space: nowrap; }
        .qqm-table tbody td { padding: 11px 16px; border-bottom: 1px solid #f1f5f9; color: #334155;
          vertical-align: middle; font-size: 0.82rem; }
        .qqm-table tbody tr { transition: background 0.14s ease; }
        .qqm-table tbody tr:last-child td { border-bottom: none; }
        .qqm-table tbody tr:hover { background: #f8fafc; }
        .qqm-idx { color: #94a3b8; font-weight: 600; font-variant-numeric: tabular-nums; }
        .qqm-avatar { width: 34px; height: 34px; border-radius: 10px; display: inline-flex;
          align-items: center; justify-content: center; color: #fff; font-weight: 700;
          font-size: 0.8rem; flex-shrink: 0; box-shadow: 0 2px 5px rgba(15,23,42,0.18); }
        .qqm-name { font-weight: 600; color: #0f172a; }
        .qqm-code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.73rem;
          font-weight: 600; color: #475569; background: #f1f5f9; padding: 2px 9px; border-radius: 6px; }
        .qqm-marks { font-weight: 700; font-variant-numeric: tabular-nums; color: #0f172a; }
        .qqm-marks-total { color: #94a3b8; font-weight: 500; }
        .qqm-muted { color: #cbd5e1; }
        .qqm-time { font-size: 0.76rem; color: #64748b; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .qqm-att { display: inline-flex; align-items: center; justify-content: center; min-width: 24px;
          height: 22px; padding: 0 7px; border-radius: 999px; background: #eef2ff; color: #4338ca;
          font-size: 0.74rem; font-weight: 700; font-variant-numeric: tabular-nums; }
        .qqm-icon-btn { width: 30px; height: 30px; border-radius: 8px; display: inline-flex;
          align-items: center; justify-content: center; cursor: pointer; font-size: 0.95rem;
          color: #64748b; transition: all 0.14s ease; margin: 0 1px; border: 1px solid transparent; }
        .qqm-icon-btn:hover { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; }
        .qqm-icon-danger:hover { background: #fef2f2; color: #dc2626; border-color: #fecaca; }
        .qqm-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
        .qqm-pulse { animation: qqmPulse 1.6s ease-in-out infinite; }
        @keyframes qqmPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.5); }
          50% { box-shadow: 0 0 0 5px rgba(245,158,11,0); } }
        .qqm-bar-track { height: 6px; background: #eef2f6; border-radius: 999px; overflow: hidden; }
        .qqm-bar-fill { height: 100%; border-radius: 999px;
          background: var(--gradient-brand); transition: width 0.3s ease; }
      `}</style>
      <Card className="qqm-monitor-card">
        <Card.Body style={{ padding: "0" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="qqm-table">
              <thead>
                <tr>
                  <th style={{ width: "48px", textAlign: "center" }}>#</th>
                  <th>Student</th>
                  <th>Code</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                  <th style={{ textAlign: "center" }}>Marks</th>
                  <th style={{ textAlign: "center" }}>Progress</th>
                  <th style={{ textAlign: "center" }}>Attempts</th>
                  <th>Submitted At</th>
                  <th>Last Activity</th>
                  <th style={{ textAlign: "center" }}>Audit</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} style={{ textAlign: "center", color: "var(--text-faint)", padding: "40px 0" }}>
                      {students.length === 0
                        ? "No students have been assigned this test yet."
                        : "No students match the current filters."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((s, i) => {
                    const pct = s.questionsTotal > 0
                      ? Math.min(100, Math.round((s.questionsAnswered / s.questionsTotal) * 100))
                      : 0;
                    return (
                      <tr key={s.studentTestSectionId || `${s.enquiryId}-${i}`}>
                        <td style={{ textAlign: "center" }}><span className="qqm-idx">{i + 1}</span></td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span className="qqm-avatar" style={{ background: avatarColor(s.studentName) }}>
                              {s.studentName?.trim()?.[0]?.toUpperCase() || "?"}
                            </span>
                            <span className="qqm-name">{s.studentName}</span>
                          </div>
                        </td>
                        <td>{s.studentCode ? <span className="qqm-code">{s.studentCode}</span> : <span className="qqm-muted">—</span>}</td>
                        <td style={{ textAlign: "center" }}>
                          <StatusBadge label={s.statusLabel} />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {s.marks != null ? (
                            <span className="qqm-marks">{s.marks}<span className="qqm-marks-total"> / {s.totalMarks}</span></span>
                          ) : (
                            <span className="qqm-muted">—</span>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {s.questionsTotal > 0 ? (
                            <div style={{ minWidth: "96px", margin: "0 auto" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", color: "var(--text-muted)", marginBottom: "4px" }}>
                                <span style={{ fontWeight: 600 }}>{s.questionsAnswered}/{s.questionsTotal}</span>
                                {s.markedForReview > 0 && (
                                  <span style={{ color: "var(--status-warning)", fontWeight: 600 }} title={`${s.markedForReview} marked for review`}>
                                    &#9733;{s.markedForReview}
                                  </span>
                                )}
                              </div>
                              <div className="qqm-bar-track"><div className="qqm-bar-fill" style={{ width: `${pct}%` }} /></div>
                            </div>
                          ) : (
                            <span className="qqm-muted">—</span>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {s.attempts ? <span className="qqm-att">{s.attempts}</span> : <span className="qqm-muted">—</span>}
                        </td>
                        <td><span className="qqm-time">{formatDateTime(s.entryDate)}</span></td>
                        <td><span className="qqm-time">{formatDateTime(s.lastActivity)}</span></td>
                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                          <span className="qqm-icon-btn" title="Audit Trail" onClick={() => handleShowAudit(s)}>&#9432;</span>
                          <span className="qqm-icon-btn" title="Web Proctoring" onClick={() => handleWebProctoring(s)}>&#128247;</span>
                          <span className="qqm-icon-btn" title="DynamoDB Data" onClick={() => handleShowDynamo(s)}>&#128451;</span>
                          <span className="qqm-icon-btn" title="Populate" onClick={() => handlePopulate(s)}>&#128194;</span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span className="qqm-icon-btn qqm-icon-danger" title="Delete Responses" onClick={() => handleDeleteResponse(s)}>&#10060;</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default TestMonitoringPanel;
